import { modifier } from 'ember-modifier';

export type RevealOptions = {
  animation?: 'fade-up' | 'fade-in' | 'slide-left' | 'slide-right' | 'zoom-in' | 'draw-x' | 'draw-x-center' | 'draw-y';
  threshold?: number; // 0..1
  rootMargin?: string; // e.g. '0px 0px -10% 0px'
  once?: boolean; // reveal only once
  delay?: number; // base delay in ms
  stagger?: number; // per-child additional delay in ms
  staggerChildren?: boolean; // whether to stagger immediate children
};

function applyInitialState(el: Element, animation: RevealOptions['animation']) {
  el.classList.add('reveal-base');
  el.classList.add(`reveal-${animation ?? 'fade-up'}`);
}

function revealElement(
  el: Element,
  opts: Required<Pick<RevealOptions, 'delay' | 'stagger' | 'staggerChildren'>>
) {
  // Apply to element
  if (opts.delay > 0) {
    (el as HTMLElement).style.transitionDelay = `${opts.delay}ms`;
  }
  el.classList.add('is-revealed');

  // Optionally reveal children with stagger
  if (opts.staggerChildren) {
    const children = (el as HTMLElement).children;
    Array.from(children).forEach((child, idx) => {
      child.classList.add('reveal-child');
      (child as HTMLElement).style.transitionDelay = `${opts.delay + idx * opts.stagger}ms`;
      child.classList.add('is-revealed');
    });
  }
}

export default modifier((element: HTMLElement, _positional: unknown[], named: RevealOptions = {}) => {
  const {
    animation = 'fade-up',
    threshold = 0.15,
    rootMargin = '0px 0px -10% 0px',
    once = true,
    delay = 0,
    stagger = 60,
    staggerChildren = false,
  } = named;

  // Set initial state
  applyInitialState(element, animation);

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          revealElement(element, { delay, stagger, staggerChildren });
          if (once) {
            observer.unobserve(element);
          }
        }
      }
    },
    { threshold, root: null, rootMargin }
  );

  observer.observe(element);

  return () => {
    observer.disconnect();
    // Clean inline transition-delay but keep classes to avoid flicker on teardown
    (element as HTMLElement).style.transitionDelay = '';
  };
});
